import {DOCUMENT} from '@angular/common';
import {Component, HostListener, Inject, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {
  AbstractComponent,
  ConfirmationModalComponent,
  RequestDetailsModalComponent,
  ValidationModalComponent
} from "@components";
import {
  ADMINISTRATOR_ROLE,
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  Lookups,
  PagedFilter,
  Pages,
  Paging,
  Request,
  RequestFilter,
  RequestItemActionType,
  RequestStatus,
  RequestTypes,
  ValidatorResult
} from "@model";
import {AuthenticationService, EventService, LoaderService, RequestService} from "@services";
import {environment} from "@environment";

@Component({
    selector: 'app-list-of-requests',
    templateUrl: './list-of-requests.component.html',
    styleUrls: ['./list-of-requests.component.scss'],
    standalone: false
})
export class ListOfRequestsComponent extends AbstractComponent implements OnInit, Paging<Request> {

  @Input('requestStatuses') statuses: Lookups[] = [];
  @Input() languages: Lookups[] = [];
  @Input() requests: Request[] = [];
  @Input() userRole: string | undefined;
  @Input() countries: Lookups[] = [];

  showFabButton: boolean = false;
  selectedStatus: number | undefined;
  selectedType: number | undefined;
  isLoading: boolean = false;
  queryParamsFirstChange: boolean = true;
  hasMore: boolean = false;
  page: number = DEFAULT_PAGE_INDEX;
  pageFilter: number = 0;
  filter: PagedFilter<RequestFilter> | undefined;

  isAdministrator: boolean = false;
  isOrgAdministrator: boolean = false;
  listOfRequestsType: ListOfRequestsType = ListOfRequestsType.INCOMING;

  types: Lookups[] = [];

  constructor(private route: ActivatedRoute, private router: Router, public translocoService: TranslocoService,
              private requestService: RequestService, private eventService: EventService,
              public authenticationService: AuthenticationService, private modalService: NgbModal,
              @Inject(DOCUMENT) private document: Document, private loaderService: LoaderService) {
    super();
  }

  ngOnInit(): void {
    this.handleExploreResult(this.requests);
    this.subscribeToRouteDataChange();
    this.isAdministrator = this.authenticationService.isAdministrator();
    this.isOrgAdministrator = this.authenticationService.isOrganisersAdministrator();
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
        distinctUntilChanged()).subscribe((queryParams: any) => {
        this.selectedType = this.isTypeValid(queryParams) ? +queryParams.type : undefined;
        this.selectedStatus = this.isStatusValid(queryParams) ? +queryParams.status : undefined;
        if (!this.queryParamsFirstChange) {
          this.onExplore(false);
        }
        this.queryParamsFirstChange = false;
      })
    );
    this.subscriptions.push(
        this.route.data.subscribe(({type}: Data) => {
          this.listOfRequestsType = type ?? ListOfRequestsType.INCOMING;
          this.requestService.filterRequestTypes(this.listOfRequestsType === ListOfRequestsType.INCOMING).subscribe(types => {
            this.types = types;
          })
        }),
        this.route.data.subscribe(({userRole}: Data) => {
          this.isAdministrator = userRole === ADMINISTRATOR_ROLE;
        }),
    )
  }


  private isStatusValid(queryParams: any) {
    return Object.values(RequestStatus).filter((v: any) => !isNaN(Number(v))).map((v: any) => v.toString()).includes(queryParams.status)
  }

  private isTypeValid(queryParams: any) {
    return Object.values(RequestTypes).filter((v: any) => !isNaN(Number(v))).map((v: any) => v.toString()).includes(queryParams.type)
  }

  private changeQueryParams() {
    let queryParams: { [key: string]: any } = {};
    if (this.selectedStatus || this.selectedStatus === 0) {
      queryParams['status'] = this.selectedStatus;
    }
    if (this.selectedType || this.selectedType === 0) {
      queryParams['type'] = this.selectedType;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  private getAllowedActionsForRequest(r: Request): RequestItemActionType[] {
    let actions : RequestItemActionType[] = []
    if (this.isAdministrator || this.isOrgAdministrator) {
        if (r.requestStatus === RequestStatus.ON_HOLD) {
          actions.push(RequestItemActionType.ACCEPT, RequestItemActionType.DENY)
        }
    }
    else {
        if (this.listOfRequestsType === ListOfRequestsType.INCOMING && r.requestStatus === RequestStatus.ON_HOLD) {
          actions = [RequestItemActionType.ACCEPT, RequestItemActionType.DENY ];
        }
    }
    return actions;
  }

  @HostListener('window:scroll')
  toggleFabButton() {
    this.showFabButton = window.pageYOffset != 0;
  }


  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  private getFilter(): RequestFilter {
    let filter: RequestFilter = new RequestFilter();
    filter.status = this.selectedStatus ?? undefined;
    filter.type = this.selectedType ?? undefined;
    return filter;
  }

  private getPagedFilter(): void {
    let filter: RequestFilter = new RequestFilter();
    filter.status = this.selectedStatus ?? undefined;
    filter.type = this.selectedType ?? undefined;
    this.filter = new PagedFilter(new Pages(undefined, this.pageFilter), filter);
  }

  handleExploreResult(value: Request[], loadMore?: boolean): void {
    if (value) {
      if (value.length > DEFAULT_PAGE_SIZE) {
        value = (value.slice(0, DEFAULT_PAGE_SIZE))
        this.hasMore = true;
      } else {
        this.hasMore = false;
      }
      if (loadMore && this.requests) {
        value = this.requests.concat(value)
      }
    }
    this.setListOfItems(value);
  }

  setListOfItems(value: Request[] | null) {
    if (value && value.length > 0) {
      this.requests = value;
      this.page++;
      this.pageFilter++;
    } else {
      this.requests = [];
    }
  }

  onExplore(loadMore?: boolean) {
    if (loadMore === true) {
      this.loaderService.startLoading();
    } else {
      this.page = 1;
      this.pageFilter = 0;
      this.isLoading = true;
    }
    this.requestService.filterRequests(new PagedFilter(new Pages(undefined, this.page), this.getFilter()),
        !(this.listOfRequestsType === ListOfRequestsType.OUTGOING)).subscribe((value: Request[]) => {
      if (loadMore === true) {
        this.loaderService.dismissLoading();
      } else {
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      }
      this.handleExploreResult(value, loadMore);
    }, (_) => {
      if (loadMore === true) {
        this.loaderService.dismissLoading();
      } else {
        this.isLoading = false;
      }
    });
  }

  loadMore(): void {
    this.onExplore(true);
  }

  onStatusSelect(status: number | undefined) {
    this.selectedStatus = status;
    this.changeQueryParams()
  }

  onTypeSelect(type: number | undefined) {
    if (this.isAdministrator || this.isOrgAdministrator) {
      this.selectedType = type;
      this.changeQueryParams()
    }
  }

  onModifyRequest(request: Request, accept: boolean): void {
      let copyRequest: Request = {...request};
      copyRequest.requestStatus = accept ? RequestStatus.ACCEPTED : RequestStatus.DENIED;
      if (this.isAdministrator && copyRequest.type === RequestTypes.ORGANISER) {
        this.requestService.manageOrganiserRequest(copyRequest).subscribe((value: boolean) => {
          if (value) {
            this.requestService.fetchNewRequests.next(value);
            this.onExplore(false);
          }
        });
      } else if ((this.isAdministrator || this.isOrgAdministrator) && copyRequest.type === RequestTypes.PUBLISH_LEAGUE) {
        this.requestService.managePublishLeagueRequest(copyRequest).subscribe((value: boolean) => {
          if (value) {
            this.requestService.fetchNewRequests.next(value);
            this.onExplore(false);
          }
        });
      } else if (copyRequest.type === RequestTypes.ADD_EVENT_TO_LEAGUE) {
        this.requestService.manageAddEventToLeagueRequest(copyRequest).subscribe((value: boolean) => {
          if (value) {
            this.requestService.fetchNewRequests.next(value);
            this.onExplore(false);
          }
        });
      } else if ((this.isAdministrator || this.isOrgAdministrator) && copyRequest.type === RequestTypes.PUBLISH_EVENT) {
        this.requestService.manageEventRequest(copyRequest).subscribe((value: ValidatorResult | undefined) => {
          if (value) {
            if (value.hasErrors()) {
              const modalRef: NgbModalRef = this.modalService.open(ValidationModalComponent, {
                centered: true,
                size: 'xl'
              });
              modalRef.componentInstance.title = this.translocoService.translate("event.item.publish");
              modalRef.componentInstance.text = this.translocoService.translate("app.validation.error.event");
              modalRef.componentInstance.validatorResult = value;
              modalRef.componentInstance.showConfirmButton = true;
              modalRef.componentInstance.confirmButtonTitle = this.translocoService.translate("request.action.decline");
              modalRef.result.then((_) => {
                this.openModifyRequestDialog(request, false);
              }, (_) => {
                /// ignore
              });
            } else {
              this.requestService.fetchNewRequests.next(true);
              this.onExplore(false);
            }
          }
        });
      }
  }

  openRequestDetails(request: Request) {
    this.requestService.fetchRequestById(request.id).subscribe(fetchedRequest => {

      const modalSize = fetchedRequest?.type === RequestTypes.ORGANISER ? 'lg' : 'xl';

      if (fetchedRequest) {
        const modalRef: NgbModalRef = this.modalService.open(RequestDetailsModalComponent, {
          centered: true,
          size: modalSize,
          scrollable: true,
          windowClass: 'race-commercial-modal',
        });

        const allowedActions = this.getAllowedActionsForRequest(fetchedRequest);
        modalRef.componentInstance.actionsAllowed = allowedActions.length > 0;
        modalRef.componentInstance.isAdmin = this.isAdministrator || (this.isOrgAdministrator && fetchedRequest.type === RequestTypes.PUBLISH_EVENT);
        modalRef.componentInstance.request = fetchedRequest;
        modalRef.componentInstance.countries = this.countries;
        modalRef.componentInstance.listOfRequestsType = this.listOfRequestsType;

        modalRef.result.then((accept: boolean) => {
          this.openModifyRequestDialog(fetchedRequest,accept);
        }, (_) => {
          /// ignore
        });
      }
    });
  }

  getUserColumn(request: Request): String {
    return request.userDetail?.firstname + " " + request.userDetail?.lastname;
  }

  getOrganiserNameColumn(request: Request) {
    switch (request.type) {
      case RequestTypes.ORGANISER:
        return request.organiserRequest?.name ?? "";
      case RequestTypes.PUBLISH_EVENT:
        return request.event?.organiser?.name ?? "";
      default:
        return request.event?.organiser?.name ?? "";
    }
  }

  openModifyRequestDialog(request: Request, accept: boolean) {
    if (this.listOfRequestsType == ListOfRequestsType.INCOMING) {
      const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
        centered: true,
      });
      let requestComment: string = "";
      modalRef.componentInstance.title = this.translocoService.translate(accept ? "request.list.accept.dialog.title" : "request.list.decline.dialog.title");
      modalRef.componentInstance.text = this.translocoService.translate(accept ? "request.list.accept.dialog.text" : "request.list.decline.dialog.text");

      if (request.type === RequestTypes.ORGANISER || !accept) {
        modalRef.componentInstance.dispayTextArea = true;
        modalRef.componentInstance.textAreaPlaceholder = this.translocoService.translate("request.action.add.comment");
        modalRef.componentInstance.textAreInputEvent.subscribe((content: string) => requestComment = content);
      }

      modalRef.result.then((_) => {
        request.comment = requestComment !== "" ? requestComment : undefined;
        this.onModifyRequest(request, accept);
      }, (_) => {
        /// ignore
      });
    }
  }

  protected readonly RequestTypes = RequestTypes;
  protected readonly ListOfRequestsType = ListOfRequestsType;
  protected readonly RequestStatus = RequestStatus;
}

export enum ListOfRequestsType {
  INCOMING,
  OUTGOING
}
